import { useEffect, useRef, useState } from 'react';

import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import type { ExtractionCounts } from './issues-context';
import {
  CONTEXT_FILES_MAX_COUNT,
  CONTEXT_FILES_MAX_TOTAL_SIZE,
} from '../../../../../../../backend/src/constants/context-file-limits';
import { Icon } from '@/new/components/icon';
import { cn } from '@/new/lib/utils';

const EXTRACTION_MESSAGES = [
  'Reading your documents...',
  'Identifying key issues...',
  'Extracting parties...',
  'Pulling out additional context...',
  'Looking for relevant details...',
];

function CyclingText() {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [width, setWidth] = useState<number | undefined>();
  const measuredRef = useRef<HTMLSpanElement>(null);

  // Measure the current message width after each index change
  useEffect(() => {
    if (measuredRef.current) {
      setWidth(measuredRef.current.scrollWidth);
    }
  }, [index]);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex((prev) => (prev + 1) % EXTRACTION_MESSAGES.length);
        setVisible(true);
      }, 400);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <span
      className="inline-block overflow-hidden text-sm transition-[width] duration-400 ease-in-out"
      style={{ width: width ? `${width}px` : 'auto' }}
    >
      <span
        ref={measuredRef}
        className="inline-block bg-gradient-to-r from-gray-300 via-gray-500 to-gray-300 bg-[length:200%_auto] bg-clip-text whitespace-nowrap text-transparent transition-all duration-400"
        style={{
          animation: 'shimmer 3s linear infinite',
          opacity: visible ? 1 : 0,
          filter: visible ? 'blur(0px)' : 'blur(4px)',
        }}
      >
        {EXTRACTION_MESSAGES[index]}
      </span>
    </span>
  );
}

interface ContextFileUploaderProps {
  onSkip: () => void;
  onFilesSelected: (files: File[]) => void;
  isUploading: boolean;
  extractionCounts?: ExtractionCounts;
}

const CATEGORIES = [
  { key: 'issues' as const, label: 'Issues' },
  { key: 'parties' as const, label: 'Parties' },
  { key: 'suggestions' as const, label: 'Additional Suggestions' },
] as const;

function ExtractionCard({ label, count }: { label: string; count: number }) {
  const [pulsing, setPulsing] = useState(false);
  const prevCountRef = useRef(count);
  const active = count > 0;

  useEffect(() => {
    if (count > prevCountRef.current) {
      setPulsing(true);
      const timeout = setTimeout(() => setPulsing(false), 600);
      prevCountRef.current = count;
      return () => clearTimeout(timeout);
    }
    prevCountRef.current = count;
  }, [count]);

  return (
    <div
      className={cn(
        'relative rounded-md border bg-white px-4 py-2.5 transition-all duration-400',
        active ? 'border-gray-200' : 'border-dashed border-gray-200',
      )}
    >
      {/* Indicator dot */}
      <div
        className={`absolute -top-1 -right-1 size-2.5 rounded-full transition-colors duration-300 ${
          active ? 'bg-teal-400' : 'bg-gray-100'
        }`}
      />
      {pulsing && (
        <div
          className={`absolute -top-1 -right-1 size-2.5 animate-ping rounded-full bg-teal-400 transition-all duration-300`}
        />
      )}

      <div className="flex items-baseline justify-between gap-2">
        <span className={`text-xs transition-colors duration-400 ${active ? 'text-gray-400' : 'text-gray-400'}`}>
          {label}:
        </span>
        <span
          className={`text-xs font-medium transition-colors duration-400 ${active ? 'text-gray-900' : 'text-gray-300'}`}
        >
          {active ? count : '—'}
        </span>
      </div>
    </div>
  );
}

export function ContextFileUploader({
  onSkip,
  onFilesSelected,
  isUploading,
  extractionCounts,
}: ContextFileUploaderProps) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: true,
    maxFiles: CONTEXT_FILES_MAX_COUNT,
    accept: { 'application/pdf': ['.pdf'] },
    disabled: isUploading,
    onDropAccepted: (files) => onFilesSelected(files),
    onDropRejected: (errors) => {
      if (errors?.[0]?.errors?.[0]?.code === 'file-invalid-type') {
        toast.error('Failed to upload files. Only PDFs are currently supported.');
        return;
      }
      toast.error('Failed to upload files');
    },
  });
  const { t } = useTranslation();

  return (
    <div className="mt-24 flex w-full flex-col items-center">
      <div className="relative mb-9">
        <div
          {...getRootProps()}
          className={cn(
            isDragActive ? 'bg-gray-100' : 'bg-gray-50',
            'relative h-48 w-sm cursor-pointer overflow-hidden rounded-xl border border-dashed border-gray-300 transition-colors hover:border-gray-400 hover:bg-gray-100/50',
          )}
        >
          <input {...getInputProps()} />

          <p
            className={`mt-10 flex items-center justify-center gap-2 text-gray-500 transition-all duration-200 ${isUploading ? 'opacity-0' : ''}`}
          >
            <Icon className="lucide--maximize" /> Drag documents here
          </p>
          <p
            className={`mt-0.75 text-center text-xs text-gray-400 transition-all duration-200 ${isUploading ? 'opacity-0' : ''}`}
          >
            Max {CONTEXT_FILES_MAX_COUNT} PDF files/{CONTEXT_FILES_MAX_TOTAL_SIZE / 1024 / 1024}MB
          </p>
          <div
            className={`absolute left-1/2 mt-7 flex -translate-x-1/2 transition-all duration-200 ${isUploading ? 'opacity-0' : ''}`}
          >
            <div className="mt-4 h-42 w-32 rounded-md border bg-white px-4 py-3 font-serif">
              <p className="text-[11px] font-bold">Client Briefing</p>
              <p className="mt-1 text-[9px] leading-tight text-gray-600">
                From: Sarah Mitchell &lt;s.mitchell@hartley.com&gt;
              </p>
              <p className="text-[9px] leading-tight text-gray-600">To: David Chen &lt;d.chen@hartley.com&gt;</p>
              <p className="text-[9px] leading-tight text-gray-600">Re: Graystone Holdings — Case Update</p>
              <p className="mt-1 text-[9px] leading-tight text-gray-600">
                David, following our call with the client this morning, please find attached the revised timeline and
                supporting documents for review ahead of Friday's hearing.
              </p>
            </div>
            <div className="-ml-14 h-42 w-36 rounded-md border bg-white px-4 py-3 font-serif shadow-lg/4">
              <p className="text-center text-[11px] font-bold capitalize">{t('claim form')}</p>
              <p className="my-1.5 text-center text-[9px] leading-tight text-gray-500">
                Directors Disqualification Application
              </p>
              <p className="mt-1.5 text-[9px] leading-tight text-gray-600">
                <span className="font-semibold">In the matter of</span> Graystone Holdings Ltd
              </p>
              <p className="mt-1 text-[9px] leading-tight text-gray-600">
                And in the matter of The Company Directors Disqualification Act 1986.
              </p>
              <p className="mt-1 text-[9px] leading-tight text-gray-600">
                <span className="font-semibold">Name of Claimant:</span> Secretary of State for Business
              </p>
              <p className="mt-1 text-[9px] leading-tight text-gray-600">
                <span className="font-semibold">Name of Defendant:</span> R. J. Thornton
              </p>
              <p className="mt-1.5 text-[10px] font-bold">The Hearing</p>
              <p className="mt-0.5 text-[9px] leading-tight text-gray-600">
                The defendant must attend before the Registrar on the date and time specified below for the hearing of
                this application.
              </p>
            </div>
          </div>

          {/* Extraction overlay */}
          {isUploading && (
            <div className="animate-fadeIn absolute inset-0 flex items-center justify-center px-8">
              {extractionCounts &&
              (extractionCounts.issues > 0 || extractionCounts.parties > 0 || extractionCounts.suggestions > 0) ? (
                <div className="animate-fadeIn flex min-w-full flex-col gap-2.5">
                  {CATEGORIES.map((cat) => (
                    <ExtractionCard key={cat.key} label={cat.label} count={extractionCounts?.[cat.key] ?? 0} />
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <Icon className="lucide--loader animate-spin text-base text-gray-400 ease-in-out" />
                  <CyclingText />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <p className="font-serif text-2xl font-medium">Add files so Wexler understands your Matter</p>
      <p className="mt-3 max-w-3xl text-center text-balance text-gray-500">
        Wexler uses issues and additional context to classify the relevance of facts. Try uploading a {t('claim form')},
        client briefing, or memo to automatically extract context from your documents.
      </p>

      {!isUploading && (
        <p className="mt-8 text-center text-xs text-gray-500">
          <button onClick={onSkip} className="inline-flex items-center gap-1 text-blue-500">
            Go straight to issues & context <Icon className="lucide--arrow-right" />
          </button>
        </p>
      )}
    </div>
  );
}
